import React, { useEffect, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  IPV4_ACL_IP_MODE_OPTIONS,
  IPV4_ACL_PORT_MODE_OPTIONS,
} from "../util/const";
import { getEndpointValidation } from "../util/func";
import DropdownInputGroup from "./DropdownInputGroup";
import SegmentedIpv4Input from "./SegmentedIpv4Input";

const FieldRow = ({ label, children }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", minHeight: 46 }}>
      <Typography
        component="div"
        sx={{
          width: 180,
          pt: "6px",
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[2],
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  );
};

const getBorderlessTextFieldSx = (theme, disabled = false, width = "100%") => ({
  width,
  "& .MuiInputBase-root": {
    height: 29,
    backgroundColor: disabled
      ? theme.palette.primary.greyThread
      : theme.palette.primary.white,
  },
  "& .MuiInputBase-input": {
    px: 1.5,
    py: 0,
    color: theme.palette.primary.greyPrimary,
    fontSize: theme.typography.customFontSize[2],
  },
  "& .MuiInput-underline:before, & .MuiInput-underline:after": {
    borderBottom: "none",
  },
  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
    borderBottom: "none",
  },
  "& .MuiInput-underline.Mui-disabled:before": {
    borderBottom: "none",
  },
});

const AclEndpointSection = ({
  title,
  endpoint,
  onChange,
}) => {
  const theme = useTheme();
  const [touched, setTouched] = useState({
    ipAddress: false,
    prefixLength: false,
    port: false,
  });
  const isAnyAddress = endpoint.ipMode === "any";
  const isAnyPort = endpoint.portMode === "any";
  const validation = getEndpointValidation(endpoint);
  const showIpAddressError = touched.ipAddress && validation.ipAddressHasError;
  const showPrefixLengthError = touched.prefixLength && validation.prefixLengthHasError;
  const showPortError = touched.port && validation.portHasError;

  useEffect(() => {
    setTouched({
      ipAddress: false,
      prefixLength: false,
      port: false,
    });
  }, [endpoint.ipMode, endpoint.portMode]);

  const updateEndpoint = (key, value) => {
    if (key === "ipMode" && value === "any") {
      onChange({
        ...endpoint,
        ipMode: value,
        ipAddress: null,
        prefixLength: 32,
      });
      return;
    }

    if (key === "ipMode" && value === "specific") {
      onChange({
        ...endpoint,
        ipMode: value,
        ipAddress: endpoint.ipAddress ?? ["", "", "", ""],
        prefixLength: endpoint.prefixLength ?? "",
      });
      return;
    }

    if (key === "portMode" && value === "any") {
      onChange({
        ...endpoint,
        portMode: value,
        port: null,
      });
      return;
    }

    onChange({
      ...endpoint,
      [key]: value,
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        sx={{
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[2],
          fontWeight: theme.typography.customFontWeight[3],
          mb: 1,
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          border: `1px solid ${theme.palette.primary.greyThread}`,
          borderRadius: theme.shape.customBorderRadius[1],
          px: 3,
          py: 2,
        }}
      >
        <FieldRow label="IP Address">
          <DropdownInputGroup
            value={endpoint.ipMode}
            onChange={(option) => updateEndpoint("ipMode", option.value)}
            options={IPV4_ACL_IP_MODE_OPTIONS}
            error={showIpAddressError}
            helperText={showIpAddressError ? "Invalid IP address" : ""}
            inputDisabled={isAnyAddress}
          >
            <SegmentedIpv4Input
              value={endpoint.ipAddress}
              displayValue={isAnyAddress ? [0, 0, 0, 0] : undefined}
              width="100%"
              disabled={isAnyAddress}
              borderless
              onChange={(value) => {
                setTouched((prev) => ({ ...prev, ipAddress: true }));
                updateEndpoint("ipAddress", value);
              }}
            />
          </DropdownInputGroup>
        </FieldRow>

        <FieldRow label="Prefix Length">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              value={isAnyAddress ? "" : endpoint.prefixLength ?? ""}
              disabled={isAnyAddress}
              error={showPrefixLengthError}
              helperText={showPrefixLengthError ? "0-32" : ""}
              onChange={(event) => {
                setTouched((prev) => ({ ...prev, prefixLength: true }));
                updateEndpoint("prefixLength", event.target.value);
              }}
              inputProps={{ inputMode: "numeric", min: 0, max: 32 }}
              sx={{
                ...getBorderlessTextFieldSx(theme, isAnyAddress, "300px"),
                flex: "0 0 300px",
                maxWidth: "300px",
              }}
            />
            <Typography
              sx={{
                pt: "5px",
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
              }}
            >
              (0-32)
            </Typography>
          </Box>
        </FieldRow>

        <FieldRow label="Port">
          <DropdownInputGroup
            value={endpoint.portMode}
            onChange={(option) => updateEndpoint("portMode", option.value)}
            options={IPV4_ACL_PORT_MODE_OPTIONS}
            error={showPortError}
            helperText={showPortError ? "Invalid format" : ""}
            inputDisabled={isAnyPort}
          >
            <TextField
              variant="standard"
              value={endpoint.portMode === "manual" ? endpoint.port ?? "" : ""}
              disabled={isAnyPort}
              onChange={(event) => {
                setTouched((prev) => ({ ...prev, port: true }));
                updateEndpoint("port", event.target.value);
              }}
              inputProps={{ inputMode: "numeric" }}
              sx={getBorderlessTextFieldSx(theme, isAnyPort)}
            />
          </DropdownInputGroup>
        </FieldRow>
      </Box>
    </Box>
  );
};

export default AclEndpointSection;
